import { Query } from 'apollo-angular';
import { Map, Seq } from 'immutable';
import { uniq } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { TcsEntity } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { cacheFactory, TimedCache } from 'src/util/cache';
import { DefaultContext } from '@apollo/client/core';
import { StoreService } from './store.service';

export type EntityFetchPolicy = 'cache-or-network' | 'network-then-cache';

export interface QueryOptions {
  fetchPolicy?: EntityFetchPolicy;
  context?: DefaultContext & { useSecondarySource?: boolean };
}

function getFetchPolicyOrDefault(
  options: QueryOptions | undefined,
  defaultPolicy: EntityFetchPolicy,
): EntityFetchPolicy {
  if (options != null && options.fetchPolicy != null) {
    return options.fetchPolicy;
  }
  return defaultPolicy;
}

export abstract class EntityFetchService<T extends { id: string }> {
  private _networkCache: TimedCache<string, Observable<T | undefined> | undefined>;

  constructor() {
    this._networkCache = cacheFactory.cache<Observable<T | undefined> | undefined>(60_000 * 15);
  }

  public getByIds(ids: string[], options?: QueryOptions): Observable<Seq.Indexed<NonNullable<T>>> {
    ids = uniq(ids);
    const fetchPolicy = getFetchPolicyOrDefault(options, 'cache-or-network');
    if (ids.length === 0) {
      return of(Seq.Indexed<NonNullable<T>>());
    }
    if (fetchPolicy === 'network-then-cache') {
      return this._getManyFromNetwork(ids, options).pipe(
        switchMap(() =>
          this._getManyFromCache(ids).pipe(
            map((d) =>
              d
                .valueSeq()
                .filter((v) => v != null)
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .map((v) => v!),
            ),
          ),
        ),
      );
    }

    return this._getManyFromCache(ids).pipe(
      switchMap((fromCache) => {
        const missingIds = fromCache
          .filter((c) => c == null)
          .keySeq()
          .toArray();

        if (missingIds.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const cacheResult = fromCache.valueSeq().map((e) => e!);
          return of(cacheResult);
        }

        return this._getManyFromNetwork(missingIds, options).pipe(
          shareReplaySafe(),
          map((fromNetwork) => {
            // merge with cache
            const merged = fromCache
              .mapEntries(([id, cached]) => {
                const cacheOrNetwork =
                  cached != null ? cached : fromNetwork.find((nr) => nr.id === id);
                return [id, cacheOrNetwork];
              })
              .filter((v) => v != null)
              .valueSeq()
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              .map((e) => e!);
            return merged;
          }),
        );
      }),
    );
  }

  public getById(id: string, options?: QueryOptions): Observable<T | undefined> {
    const fetchPolicy = getFetchPolicyOrDefault(options, 'cache-or-network');
    if (fetchPolicy === 'network-then-cache') {
      return this._getFromNetworkCached(id, options).pipe(
        switchMap(() => this._getFromCache(id)),
        distinctUntilChanged(),
      );
    }
    return this._getFromCache(id).pipe(
      distinctUntilChanged(),
      switchMap((cached) =>
        cached != null ? of(cached) : this._getFromNetworkCached(id, options),
      ),
    );
  }

  private _getFromNetworkCached(
    id: string,
    queryOptions?: QueryOptions,
  ): Observable<T | undefined> {
    let networkResult = this._networkCache.get(id);
    if (networkResult != null) {
      return networkResult;
    }
    networkResult = this._getFromNetwork(id, queryOptions).pipe(
      catchError((err) => {
        this._networkCache.delete(id);
        return throwError(err);
      }),
      tap(() => {
        this._networkCache.delete(id);
      }),
      shareReplaySafe(),
    );
    this._networkCache.set(id, networkResult);
    return networkResult;
  }

  protected abstract _getManyFromNetwork(
    ids: string[],
    queryOptions?: QueryOptions,
  ): Observable<T[]>;
  protected abstract _getFromNetwork(
    id: string,
    queryOptions?: QueryOptions,
  ): Observable<T | undefined>;
  protected abstract _getFromCache(id: string): Observable<T | undefined>;
  protected abstract _getManyFromCache(ids: string[]): Observable<Map<string, T | undefined>>;
}

export abstract class ClientSyncFetchService<
  T extends { id: string },
> extends EntityFetchService<T> {
  constructor(
    private readonly _getByIdQuery: Query<any, any>,
    private readonly _getByIdsQuery: Query<any, any>,
    private readonly _store: StoreService,
    private readonly _selectByIdFactory: (id: string) => (state: AppState) => T | undefined,
    private readonly _selectManyFactory: (
      ids: string[],
    ) => (state: AppState) => Map<string, T | undefined>,
    private readonly _recordFactory: (json: any) => T,
  ) {
    super();
  }

  protected _getFromNetwork(id: string, queryOptions?: QueryOptions): Observable<T | undefined> {
    return this._getByIdQuery.fetch({ id }, { ...queryOptions, fetchPolicy: 'no-cache' }).pipe(
      filter((r) => !r.loading),
      map((r) => {
        const fromNetwork = Object.values<any>(r.data)[0].byId;
        if (fromNetwork != null) {
          this._updateCache([fromNetwork]);
          const record = this._recordFactory(fromNetwork);
          return record;
        }
        return undefined;
      }),
      distinctUntilChanged(),
    );
  }

  public getMany(
    {
      cacheSelector,
      getIdsFallback,
    }: {
      cacheSelector: (state: AppState) => Seq.Indexed<T>;
      getIdsFallback: Observable<string[]>;
    },
    extras: { source?: string } = {},
  ): Observable<Seq.Indexed<T>> {
    return this._store.store.select(cacheSelector).pipe(
      switchMap((entities) => {
        if (entities.count() === 0) {
          return getIdsFallback.pipe(
            switchMap((ids) => {
              return this.getByIds(ids).pipe(
                map((r) => {
                  return r;
                }),
              );
            }),
          );
        }
        return of(entities);
      }),
    );
  }

  private _updateCache(networkResults: TcsEntity[]) {
    this._store.upsertEntities(networkResults);
  }

  protected _getManyFromNetwork(ids: string[], queryOptions?: QueryOptions): Observable<T[]> {
    ids = uniq(ids);
    return this._getByIdsQuery.fetch({ ids }, { ...queryOptions, fetchPolicy: 'no-cache' }).pipe(
      filter((r) => !r.loading),
      map((r) => {
        const networkResults: TcsEntity[] | undefined = Object.values<any>(r.data)[0].byIds;
        if (networkResults != null) {
          this._updateCache(networkResults);
          const records = networkResults.map((nr: any) => this._recordFactory(nr));
          return records;
        }
        return [];
      }),
    );
  }

  protected _getFromCache(id: string): Observable<T | undefined> {
    return this._store.store.select(this._selectByIdFactory(id));
  }

  protected _getManyFromCache(ids: string[]): Observable<Map<string, T | undefined>> {
    ids = uniq(ids);
    return this._store.store.select(this._selectManyFactory(ids));
  }
}
